import axios from "axios";
import Vue from "vue";

// Routes shared by the auth flow. Auth0's logout returnTo needs an absolute
// URL, so callers prepend window.location.origin where required.
export const LOGIN_ROUTE = "/authentication/login";
export const AUTH0_CALLBACK_ROUTE = "/authentication/auth0-callback";
export const AUTH_ROUTE_PREFIX = "/authentication/";

// Returns a catch handler for requests whose failure must never surface to
// the user (the interceptors in main.js already handle session expiry). The
// error is still visible during development so it is not silently lost.
export const swallowError = context => error => {
  if (process.env.NODE_ENV !== "production") {
    console.warn(`[PO] Ignored request failure (${context}):`, error);
  }
};

let validationInFlight = null;
let validatedUntil = 0;

export function clearPoSession() {
  [
    "token",
    "tokens",
    "users",
    "isAuthenticated",
    "site_id",
    "active_menu_paths",
  ].forEach(key => localStorage.removeItem(key));
  validationInFlight = null;
  validatedUntil = 0;
}

export function poToken() {
  return localStorage.getItem("token") || "";
}

export function installPoBearerInterceptor() {
  axios.interceptors.request.use(config => {
    const token = poToken();
    let sameOrigin = false;
    try {
      sameOrigin = new URL(config.url || "", window.location.origin).origin
        === window.location.origin;
    } catch (error) {
      sameOrigin = false;
    }
    if (token && sameOrigin) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

function isTransientSessionError(error) {
  if (!error || error.poNotAuthenticated) return false;
  const status = error.response ? error.response.status : null;
  return status === null || status === 429 || status >= 500;
}

export async function validatePoSession(force = false) {
  if (!poToken()) return null;
  if (!force && Date.now() < validatedUntil) {
    return { authenticated: true };
  }
  if (validationInFlight) return validationInFlight;

  validationInFlight = axios
    .get(Vue.prototype.$apiurl + "api/web/common/auth-check", { timeout: 30000 })
    .then(response => {
      const payload = response.data && response.data.data
        ? response.data.data
        : response.data;
      if (
        !payload ||
        payload.authenticated !== true ||
        payload.site_id === undefined ||
        payload.site_id === null
      ) {
        const notAuthenticated = new Error("The PO session response was not authoritative.");
        notAuthenticated.poNotAuthenticated = true;
        throw notAuthenticated;
      }
      validatedUntil = Date.now() + 30000;
      return payload;
    })
    .catch(error => {
      // Auth0 Dev item H: a slow or failed check (timeout, network, 5xx) says
      // nothing about the session, so it must not log the user out. Only an
      // authoritative answer ends it; a 401 is also cleared by the main.js
      // response interceptor.
      if (isTransientSessionError(error)) {
        error.poTransient = true;
        throw error;
      }
      clearPoSession();
      throw error;
    })
    .finally(() => {
      validationInFlight = null;
    });

  return validationInFlight;
}

// Fetches the site's auth configuration. Fails closed: with no authoritative
// answer, assume Auth0-only and not ready so no local login form is offered
// and no Auth0 write operations are attempted.
export async function fetchAuthConfig(apiUrl) {
  try {
    const response = await axios.get(apiUrl + "api/web/site/auth-config");
    const config = response.data.data;
    return {
      auth0Only: config.method === "auth0",
      auth0Ready: config.auth0_ready === true,
    };
  } catch (error) {
    return { auth0Only: true, auth0Ready: false };
  }
}

// Auth0 lifecycle states from which the hosted invite may be (re)sent.
export const AUTH0_INVITEABLE_STATES = ["pending", "reconciliation_required"];

export function newCorrelationId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  const random = Array.from(bytes)
    .map(value => value.toString(16).padStart(2, "0"))
    .join("");
  return `po:${Date.now().toString(36)}:${random}`;
}

// Shared by the admin user-management views: Auth0-mode detection plus
// per-operation correlation ids. An id is memoised per operation key so a
// retry of the same operation reuses it, letting the backend deduplicate;
// it is cleared once the operation settles server-side.
export const poAuthAdminMixin = {
  data() {
    return {
      auth0Only: true,
      auth0Ready: false,
      operationCorrelations: {},
    };
  },
  methods: {
    correlationId(operation) {
      if (!this.operationCorrelations[operation]) {
        this.$set(this.operationCorrelations, operation, newCorrelationId());
      }
      return this.operationCorrelations[operation];
    },
    clearCorrelationId(operation) {
      this.$delete(this.operationCorrelations, operation);
    },
    async getAuthMode() {
      const config = await fetchAuthConfig(this.$apiurl);
      this.auth0Only = config.auth0Only;
      this.auth0Ready = config.auth0Ready;
    },
    // Auth0 states the hosted invite can be (re)sent from. 'pending' is the
    // normal first send; 'reconciliation_required' is the recovery path — the
    // backend send-auth0-invite endpoint deliberately does not gate on
    // auth0_state, and a confirmed invite returns the user to 'invited',
    // which is login-eligible again.
    canSendAuth0Invite(user) {
      return Boolean(
        user &&
          this.auth0Only &&
          user.auth0_linked &&
          AUTH0_INVITEABLE_STATES.includes(user.auth0_state)
      );
    },
    // Shared by the users list, detail and edit views. Confirms, calls the
    // endpoint, reports the outcome, and resolves to the new auth0_state (or
    // null when the admin cancelled or the call failed) so each caller can
    // refresh itself however it loads the user.
    async sendAuth0Invite(user) {
      const retrying = user.auth0_state === "reconciliation_required";
      const confirmation = await this.$swal.fire({
        title: retrying ? "Retry Auth0 invite?" : "Send Auth0 invite?",
        text: retrying
          ? `This user is in reconciliation_required and cannot log in with Auth0. Auth0 will send its hosted password-setup email to ${user.email}; if Auth0 confirms, the user is returned to 'invited' and can log in again.`
          : `Auth0 will send its hosted password-setup email to ${user.email}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: retrying ? "Retry invite" : "Send invite",
      });
      if (!confirmation.value) return null;
      const correlationKey = `invite:${user.id}`;
      try {
        const response = await axios.post(
          this.$apiurl + "api/web/site/send-auth0-invite",
          {
            user_id: user.id,
            correlation_id: this.correlationId(correlationKey),
          }
        );
        const state = response.data.data.state;
        this.$swal.fire(
          "Invite requested",
          state === "invited"
            ? "Auth0 accepted the hosted invite request."
            : "The request needs reconciliation.",
          state === "invited" ? "success" : "warning"
        );
        this.clearCorrelationId(correlationKey);
        return state;
      } catch (error) {
        this.$swal.fire(
          "Invite failed",
          "No invite state was assumed. Reconciliation is required.",
          "error"
        );
        return null;
      }
    },
  },
};
